// AdminFragments — superAdmin/Admin browser for R2 fragment files and
// their FragmentHash DB rows. Search + paginated table + per-row
// inspect + single/bulk delete with R2/DB/both target.
//
// Hits backend routes mounted at /admin/fragments (JWT + admin guard
// applied server-side; this page also checks the redux admin flag so
// non-admins don't even render).
//
// Wired into App.jsx behind the maybeMainAdmin flag.

import React, { useEffect, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import { Redirect } from "react-router-dom";
import { url, tokenRef } from "api";

const PAGE_SIZE_DEFAULT = 50;

function authHeaders() {
  // Env-scoped key from api/index.js (bidlight_token / prebidlight_token).
  const token = localStorage.getItem(tokenRef);
  return {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

async function api(endpoint, init = {}) {
  const res = await fetch(`${url}${endpoint}`, {
    ...init,
    headers: { ...authHeaders(), ...(init.headers || {}) },
  });
  const text = await res.text();
  let json = null;
  try { json = text ? JSON.parse(text) : null; } catch (_) { /* non-JSON response */ }
  if (!res.ok) {
    const msg = (json && (json.error || json.message)) || text || `HTTP ${res.status}`;
    throw new Error(msg);
  }
  return json;
}

function fmtBytes(n) {
  if (n == null || isNaN(n)) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
}

function fmtDate(d) {
  if (!d) return "—";
  return new Date(d).toLocaleString();
}

export default function AdminFragments() {
  const userInfoRedux = useSelector((s) => s.user?.userInfo);
  const isAdmin = !!userInfoRedux?.admin;

  // List state
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Query state
  const [q, setQ] = useState("");
  const [since, setSince] = useState("");
  const [until, setUntil] = useState("");
  const [sort, setSort] = useState("lastSeenAt");
  const [order, setOrder] = useState("desc");
  const [page, setPage] = useState(1);
  const [pageSize] = useState(PAGE_SIZE_DEFAULT);

  // Selection state
  const [selected, setSelected] = useState(() => new Set());

  // Per-row detail modal state
  const [detailHash, setDetailHash] = useState(null);
  const [detailData, setDetailData] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(null);

  // Delete confirm modal state
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState("both"); // 'both' | 'r2' | 'db'
  const [deleteConfirm, setDeleteConfirm] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [deleteResult, setDeleteResult] = useState(null);

  // Delete-ALL modal state (separate flow from selection-bulk delete —
  // wipes the entire fragments/ prefix on R2 + every FragmentHash row.
  // Stricter confirmation token because there is no scoping).
  const [deleteAllOpen, setDeleteAllOpen] = useState(false);
  const [deleteAllTarget, setDeleteAllTarget] = useState("both");
  const [deleteAllConfirm, setDeleteAllConfirm] = useState("");
  const [deletingAll, setDeletingAll] = useState(false);
  const [deleteAllResult, setDeleteAllResult] = useState(null);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const loadPage = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (q.trim()) params.set("q", q.trim());
      if (since) params.set("since", since);
      if (until) params.set("until", until);
      params.set("sort", sort);
      params.set("order", order);
      params.set("page", String(page));
      params.set("limit", String(pageSize));

      const data = await api(`/admin/fragments?${params.toString()}`);
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (err) {
      setError(err.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, [q, since, until, sort, order, page, pageSize]);

  useEffect(() => { if (isAdmin) loadPage(); }, [isAdmin, loadPage]);

  if (!isAdmin) return <Redirect to="/personal-area" />;

  const allOnPageSelected = items.length > 0 && items.every((it) => selected.has(it.hash));
  function toggleSelectAll() {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allOnPageSelected) items.forEach((it) => next.delete(it.hash));
      else items.forEach((it) => next.add(it.hash));
      return next;
    });
  }
  function toggleOne(hash) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(hash)) next.delete(hash); else next.add(hash);
      return next;
    });
  }
  function clearSelection() { setSelected(new Set()); }

  async function openDetails(hash) {
    setDetailHash(hash);
    setDetailData(null);
    setDetailError(null);
    setDetailLoading(true);
    try {
      const data = await api(`/admin/fragments/${encodeURIComponent(hash)}`);
      setDetailData(data);
    } catch (err) {
      setDetailError(err.message || "Failed to load details");
    } finally {
      setDetailLoading(false);
    }
  }
  function closeDetails() {
    setDetailHash(null); setDetailData(null); setDetailError(null);
  }

  function openDeleteModal() {
    if (selected.size === 0) return;
    setDeleteOpen(true);
    setDeleteTarget("both");
    setDeleteConfirm("");
    setDeleteResult(null);
  }
  function closeDeleteModal() {
    setDeleteOpen(false);
    setDeleteResult(null);
  }

  async function runDelete() {
    if (deleteConfirm !== "DELETE") return;
    setDeleting(true);
    try {
      const hashes = Array.from(selected);
      const data = await api(`/admin/fragments/bulk-delete`, {
        method: "POST",
        body: JSON.stringify({ hashes, target: deleteTarget }),
      });
      setDeleteResult(data);
      clearSelection();
      // Reload current page so stats reflect the delete.
      loadPage();
    } catch (err) {
      setDeleteResult({ error: err.message || "Delete failed" });
    } finally {
      setDeleting(false);
    }
  }

  function openDeleteAllModal() {
    setDeleteAllOpen(true);
    setDeleteAllTarget("both");
    setDeleteAllConfirm("");
    setDeleteAllResult(null);
  }
  function closeDeleteAllModal() {
    setDeleteAllOpen(false);
    setDeleteAllResult(null);
  }

  async function runDeleteAll() {
    if (deleteAllConfirm !== "DELETE-ALL") return;
    setDeletingAll(true);
    try {
      const data = await api(`/admin/fragments/delete-all`, {
        method: "POST",
        body: JSON.stringify({ target: deleteAllTarget, confirm: "DELETE-ALL" }),
      });
      setDeleteAllResult(data);
      clearSelection();
      loadPage();
    } catch (err) {
      setDeleteAllResult({ error: err.message || "Delete-all failed" });
    } finally {
      setDeletingAll(false);
    }
  }

  return (
    <div style={{ padding: "20px 24px", fontFamily: "system-ui, sans-serif" }}>
      <h2 style={{ marginBottom: 4 }}>Fragment Browser <span style={{ fontSize: 13, color: "#888" }}>(admin)</span></h2>
      <p style={{ color: "#666", marginTop: 0, fontSize: 13 }}>
        Browse and delete R2 fragments + their FragmentHash registry rows.
        Operations are destructive — there is no undo.
      </p>

      {/* ---- Filters ---- */}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", margin: "12px 0", alignItems: "end" }}>
        <Field label="Hash contains">
          <input type="text" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }}
                 placeholder="hex substring" style={inputStyle} />
        </Field>
        <Field label="Last seen since">
          <input type="date" value={since} onChange={(e) => { setSince(e.target.value); setPage(1); }} style={inputStyle} />
        </Field>
        <Field label="Last seen until">
          <input type="date" value={until} onChange={(e) => { setUntil(e.target.value); setPage(1); }} style={inputStyle} />
        </Field>
        <Field label="Sort by">
          <select value={sort} onChange={(e) => setSort(e.target.value)} style={inputStyle}>
            <option value="lastSeenAt">Last seen</option>
            <option value="firstSeenAt">First seen</option>
            <option value="hash">Hash</option>
          </select>
        </Field>
        <Field label="Order">
          <select value={order} onChange={(e) => setOrder(e.target.value)} style={inputStyle}>
            <option value="desc">Descending</option>
            <option value="asc">Ascending</option>
          </select>
        </Field>
        <button onClick={loadPage} disabled={loading} style={btnPrimary}>
          {loading ? "Loading…" : "Refresh"}
        </button>
      </div>

      {/* ---- Action bar ---- */}
      <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 8 }}>
        <span style={{ fontSize: 13, color: "#444" }}>
          {total.toLocaleString()} total · page {page} of {totalPages} · {selected.size} selected
        </span>
        <button onClick={openDeleteModal} disabled={selected.size === 0} style={btnDanger}>
          Delete {selected.size > 0 ? `${selected.size} selected` : "selected"}
        </button>
        <button onClick={clearSelection} disabled={selected.size === 0} style={btnSecondary}>
          Clear selection
        </button>
        <button onClick={openDeleteAllModal} style={btnDangerStrong} title="Wipe every fragment from R2 and/or DB">
          Delete ALL ({total.toLocaleString()})
        </button>
      </div>

      {/* ---- Table ---- */}
      {error ? <div style={{ color: "#c00", padding: 8 }}>{error}</div> : null}
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
        <thead>
          <tr style={{ background: "#f5f5f5", borderBottom: "1px solid #ddd" }}>
            <th style={th}><input type="checkbox" checked={allOnPageSelected} onChange={toggleSelectAll} /></th>
            <th style={th}>Hash</th>
            <th style={th}>First seen</th>
            <th style={th}>Last seen</th>
            <th style={th}>Projects</th>
            <th style={th}></th>
          </tr>
        </thead>
        <tbody>
          {items.length === 0 && !loading ? (
            <tr><td colSpan={6} style={{ padding: 20, textAlign: "center", color: "#888" }}>No fragments match.</td></tr>
          ) : null}
          {items.map((it) => (
            <tr key={it.hash} style={{ borderBottom: "1px solid #eee" }}>
              <td style={td}>
                <input type="checkbox" checked={selected.has(it.hash)} onChange={() => toggleOne(it.hash)} />
              </td>
              <td style={{ ...td, fontFamily: "monospace", fontSize: 12 }}>
                <span title={it.hash}>{it.hash.length > 64 ? it.hash.slice(0, 64) + "…" : it.hash}</span>
              </td>
              <td style={td}>{fmtDate(it.firstSeenAt)}</td>
              <td style={td}>{fmtDate(it.lastSeenAt)}</td>
              <td style={td}>{it.projectCount}</td>
              <td style={td}>
                <button onClick={() => openDetails(it.hash)} style={btnLink}>Details</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* ---- Pagination ---- */}
      <div style={{ marginTop: 12, display: "flex", gap: 8, alignItems: "center" }}>
        <button disabled={page <= 1} onClick={() => setPage(1)} style={btnSecondary}>«</button>
        <button disabled={page <= 1} onClick={() => setPage(page - 1)} style={btnSecondary}>‹</button>
        <span style={{ fontSize: 13 }}>Page {page} / {totalPages}</span>
        <button disabled={page >= totalPages} onClick={() => setPage(page + 1)} style={btnSecondary}>›</button>
        <button disabled={page >= totalPages} onClick={() => setPage(totalPages)} style={btnSecondary}>»</button>
      </div>

      {/* ---- Detail modal ---- */}
      {detailHash ? (
        <Modal onClose={closeDetails} title="Fragment details">
          {detailLoading ? <div>Loading…</div> : null}
          {detailError ? <div style={{ color: "#c00" }}>{detailError}</div> : null}
          {detailData ? <DetailBody data={detailData} /> : null}
        </Modal>
      ) : null}

      {/* ---- Delete-ALL confirm modal ---- */}
      {deleteAllOpen ? (
        <Modal onClose={closeDeleteAllModal} title="Delete ALL fragments">
          {deleteAllResult ? (
            <DeleteAllResultBody result={deleteAllResult} onClose={closeDeleteAllModal} />
          ) : (
            <>
              <p style={{ color: "#b00020" }}>
                <strong>Danger.</strong> This wipes <strong>every</strong> fragment
                (~{total.toLocaleString()} DB rows + everything under R2 prefix <code>fragments/</code>).
                The next export will rebuild all fragment GLBs from scratch — this is exactly what
                you want after an exporter geometry/offset fix, but it forces every project's
                viewer to refetch from R2 on first open.
              </p>
              <label style={{ display: "block", margin: "8px 0" }}>
                Target:&nbsp;
                <select value={deleteAllTarget} onChange={(e) => setDeleteAllTarget(e.target.value)} style={inputStyle}>
                  <option value="both">Both R2 and DB (recommended)</option>
                  <option value="r2">R2 only (DB rows kept — exporter will think files still exist and skip re-upload)</option>
                  <option value="db">DB only (R2 files kept — orphans, but exporter re-registers on next push)</option>
                </select>
              </label>
              <label style={{ display: "block", margin: "12px 0 4px" }}>
                Type <code>DELETE-ALL</code> to confirm:
              </label>
              <input type="text" value={deleteAllConfirm} onChange={(e) => setDeleteAllConfirm(e.target.value)} style={inputStyle} />
              <div style={{ marginTop: 12, display: "flex", gap: 8, justifyContent: "flex-end" }}>
                <button onClick={closeDeleteAllModal} style={btnSecondary}>Cancel</button>
                <button onClick={runDeleteAll} disabled={deleteAllConfirm !== "DELETE-ALL" || deletingAll} style={btnDangerStrong}>
                  {deletingAll ? "Deleting…" : "Wipe everything"}
                </button>
              </div>
            </>
          )}
        </Modal>
      ) : null}

      {/* ---- Delete confirm modal ---- */}
      {deleteOpen ? (
        <Modal onClose={closeDeleteModal} title={`Delete ${selected.size} fragment${selected.size === 1 ? "" : "s"}`}>
          {deleteResult ? (
            <DeleteResultBody result={deleteResult} onClose={closeDeleteModal} />
          ) : (
            <>
              <p>This action is <strong>irreversible</strong>.</p>
              <label style={{ display: "block", margin: "8px 0" }}>
                Target:&nbsp;
                <select value={deleteTarget} onChange={(e) => setDeleteTarget(e.target.value)} style={inputStyle}>
                  <option value="both">Both R2 and DB (recommended)</option>
                  <option value="r2">R2 only (advanced — leaves orphan DB row)</option>
                  <option value="db">DB only (advanced — leaves orphan R2 file)</option>
                </select>
              </label>
              {deleteTarget !== "both" ? (
                <p style={{ color: "#a60", fontSize: 13 }}>
                  Asymmetric delete creates a half-state. Use only when you know the other side is already missing.
                </p>
              ) : null}
              <label style={{ display: "block", margin: "12px 0 4px" }}>
                Type <code>DELETE</code> to confirm:
              </label>
              <input type="text" value={deleteConfirm} onChange={(e) => setDeleteConfirm(e.target.value)} style={inputStyle} />
              <div style={{ marginTop: 12, display: "flex", gap: 8, justifyContent: "flex-end" }}>
                <button onClick={closeDeleteModal} style={btnSecondary}>Cancel</button>
                <button onClick={runDelete} disabled={deleteConfirm !== "DELETE" || deleting} style={btnDanger}>
                  {deleting ? "Deleting…" : "Confirm delete"}
                </button>
              </div>
            </>
          )}
        </Modal>
      ) : null}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label style={{ display: "flex", flexDirection: "column", fontSize: 12, color: "#444" }}>
      <span style={{ marginBottom: 2 }}>{label}</span>
      {children}
    </label>
  );
}

function Modal({ title, onClose, children }) {
  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)",
      display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000,
    }} onClick={onClose}>
      <div style={{
        background: "white", padding: 20, borderRadius: 6, minWidth: 420, maxWidth: 720,
        maxHeight: "calc(80 * var(--vh))", overflow: "auto",
      }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <h4 style={{ margin: 0 }}>{title}</h4>
          <button onClick={onClose} style={btnLink}>✕</button>
        </div>
        {children}
      </div>
    </div>
  );
}

function DetailBody({ data }) {
  return (
    <div>
      <div style={{ fontFamily: "monospace", fontSize: 12, wordBreak: "break-all", marginBottom: 12 }}>
        {data.hash}
      </div>

      <h5 style={{ marginBottom: 4 }}>Database</h5>
      {data.db ? (
        <ul style={{ margin: "0 0 12px 18px", fontSize: 13 }}>
          <li>First seen: {fmtDate(data.db.firstSeenAt)}</li>
          <li>Last seen: {fmtDate(data.db.lastSeenAt)}</li>
          <li>
            Projects ({(data.db.projects || []).length}):
            <ul style={{ margin: "2px 0 0 18px" }}>
              {(data.db.projects || []).length === 0 ? (
                <li style={{ color: "#888" }}>none recorded</li>
              ) : null}
              {(data.db.projects || []).map((p) => (
                <li key={p._id}>{p.title || "(untitled)"} {p.projectNumber ? `· #${p.projectNumber}` : ""}</li>
              ))}
            </ul>
          </li>
        </ul>
      ) : <p style={{ color: "#888", fontSize: 13 }}>No DB row.</p>}

      <h5 style={{ marginBottom: 4 }}>R2</h5>
      {data.r2?.status === "present" ? (
        <ul style={{ margin: "0 0 12px 18px", fontSize: 13 }}>
          <li>Key: <code>{data.r2.key}</code></li>
          <li>Size: {fmtBytes(data.r2.sizeBytes)}</li>
          <li>Last modified: {fmtDate(data.r2.lastModified)}</li>
          <li>ETag: <code>{data.r2.etag}</code></li>
        </ul>
      ) : data.r2?.status === "missing" ? (
        <p style={{ color: "#a60", fontSize: 13 }}>Missing in R2 (DB-only orphan).</p>
      ) : (
        <p style={{ color: "#c00", fontSize: 13 }}>R2 lookup failed: {data.r2?.error || "unknown"}</p>
      )}
    </div>
  );
}

function DeleteAllResultBody({ result, onClose }) {
  if (result.error) {
    return (
      <>
        <p style={{ color: "#c00" }}>{result.error}</p>
        <div style={{ textAlign: "right" }}>
          <button onClick={onClose} style={btnSecondary}>Close</button>
        </div>
      </>
    );
  }
  return (
    <>
      <p>
        Done in {(result.durationMs / 1000).toFixed(1)}s. Target: <strong>{result.target}</strong>.<br />
        R2 objects deleted: <strong>{result.r2Deleted}</strong>
        {result.r2Errors > 0 ? ` (${result.r2Errors} errors)` : ""}.<br />
        DB rows deleted: <strong>{result.dbDeleted}</strong>.
      </p>
      {result.sampleErrors && result.sampleErrors.length > 0 ? (
        <details style={{ marginTop: 8 }}>
          <summary>First {result.sampleErrors.length} error(s)</summary>
          <ul style={{ fontSize: 12 }}>
            {result.sampleErrors.map((e, i) => (
              <li key={i}><code>{e.where}</code>{e.key ? `: ${e.key}` : ""} — {e.message}</li>
            ))}
          </ul>
        </details>
      ) : null}
      <div style={{ textAlign: "right", marginTop: 12 }}>
        <button onClick={onClose} style={btnPrimary}>Close</button>
      </div>
    </>
  );
}

function DeleteResultBody({ result, onClose }) {
  if (result.error) {
    return (
      <>
        <p style={{ color: "#c00" }}>{result.error}</p>
        <div style={{ textAlign: "right" }}>
          <button onClick={onClose} style={btnSecondary}>Close</button>
        </div>
      </>
    );
  }
  return (
    <>
      <p>
        Done. Target: <strong>{result.target}</strong>.
        DB rows deleted: <strong>{result.dbDeleted}</strong>.
        R2 objects deleted: <strong>{result.r2Deleted}</strong>.
        Failed: <strong>{result.failed}</strong>.
      </p>
      {result.failed > 0 ? (
        <details style={{ marginTop: 8 }}>
          <summary>Failed hashes</summary>
          <ul style={{ fontSize: 12 }}>
            {(result.results || []).filter((r) => r.errors.length).slice(0, 50).map((r) => (
              <li key={r.hash}>
                <code>{r.hash.slice(0, 32)}…</code>: {r.errors.map((e) => `${e.where}: ${e.message}`).join("; ")}
              </li>
            ))}
          </ul>
        </details>
      ) : null}
      <div style={{ textAlign: "right", marginTop: 12 }}>
        <button onClick={onClose} style={btnPrimary}>Close</button>
      </div>
    </>
  );
}

// ---- inline styles (kept here so the page is self-contained) ----
const inputStyle = { padding: "4px 6px", border: "1px solid #ccc", borderRadius: 3, fontSize: 13 };
const btnPrimary = { padding: "6px 12px", background: "#1976d2", color: "white", border: "none", borderRadius: 3, cursor: "pointer", fontSize: 13 };
const btnDanger  = { padding: "6px 12px", background: "#d32f2f", color: "white", border: "none", borderRadius: 3, cursor: "pointer", fontSize: 13 };
const btnDangerStrong = { padding: "6px 12px", background: "#7a0000", color: "white", border: "1px solid #ff5151", borderRadius: 3, cursor: "pointer", fontSize: 13, fontWeight: 600 };
const btnSecondary = { padding: "6px 12px", background: "#eee", border: "1px solid #ccc", borderRadius: 3, cursor: "pointer", fontSize: 13 };
const btnLink = { padding: "2px 6px", background: "transparent", color: "#1976d2", border: "none", cursor: "pointer", fontSize: 13 };
const th = { padding: "8px 6px", textAlign: "left", fontWeight: 600 };
const td = { padding: "6px 6px" };
